import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  InlineNotification,
  Layer,
  ProgressBar,
  Tag,
  TextArea,
} from '@carbon/react';
import {
  CheckmarkFilled,
  ChevronDown,
  CircleDash,
  Close,
  CloseFilled,
  DocumentBlank,
  View,
} from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import classNames from 'classnames';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import type { TypeCurveWorkspaceResponse } from 'types/type-curve-workspace';
import type { WorkspaceTask } from 'types/type-curve-workspace-show';
import type { MineralType } from 'types/minerals';
import {
  tcSavedWellFiltersAtom,
  tcWellsFacetsAtom,
  tcFilteredUwisAtom,
  tcMapFilterModeAtom,
  tcMapBoundsAtom,
  tcAoiPolygonAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import { labelForFilterRow } from 'pages/type-curves/type-curve-filters/filter-labels';
import { buildWellControlAreaValue } from 'pages/type-curves/type-curve-filters/well-control-area';
import {
  tcDetailsDraftAtom,
  probitRefreshTriggerAtom,
} from 'pages/type-curves/atoms';
import {
  tcDcaFitParamsAtom,
  tcDcaFitSegmentsAtom,
  tcDcaFitAggregationAtom,
  tcDcaFitScenarioIdAtom,
  type StreamFitSegments,
} from 'pages/type-curves/type-curve-dca/dca-fit-atoms';
import { CreateDcaWorkspace } from 'components/dca-workspace/create-dca-workspace';

import {
  tcWorkspaceTasksAtom,
  tcWorkspaceTasksTotalAtom,
  tcWorkspaceTasksCompletedAtom,
  tcExpandTaskPathAtom,
  OVERALL_TYPE_CURVE_KEY,
} from './task-atoms';
import { FIELD_TASK_BY_PATH } from './field-task-map';
import { useSaveWorkspaceTask, parseTaskError } from './use-workspace-tasks';

import style from './tc-verification-tasks-panel.module.scss';

/**
 * Returns true when a persisted `ramp_up` object contains valid data that
 * should be included in the verification payload. Mirrors the completeness
 * rules from `isRampUpComplete` in `use-forecast-volumes.ts`.
 * @param {unknown} rampUp - The persisted ramp_up object to validate.
 * @return {boolean} Whether the ramp_up is valid for inclusion in the payload.
 */
function isPersistedRampUpValid(rampUp: unknown): boolean {
  if (!rampUp || typeof rampUp !== 'object') return false;
  const r = rampUp as Record<string, unknown>;
  const model = r.model;
  if (!model || model === 'none') return false;
  if (r.qi === null || r.qi === undefined || r.qi === 0) return false;
  if (r.t_ramp === null || r.t_ramp === undefined || r.t_ramp === 0)
    return false;
  if (
    (model === 'linear' || model === 'exponential') &&
    (r.n === null || r.n === undefined || r.n === 0)
  ) {
    return false;
  }
  return true;
}

/**
 * Returns true when a persisted `dca_params` object contains valid numeric
 * values for every field required by its declared model. Ranges mirror the
 * constraints in `tc-dca-validations.ts`.
 * @param {Record<string, unknown>} dcaParams - The persisted dca_params object to validate.
 * @return {boolean} Whether the DCA params are valid for inclusion in the payload.
 */
function isPersistedDcaParamsValid(
  dcaParams: Record<string, unknown>
): boolean {
  const model = dcaParams.dca_model;
  if (!model || typeof model !== 'string') return false;

  const inRange = (v: unknown, min: number, max: number): boolean =>
    typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max;

  if (model === 'ratio') {
    return inRange(dcaParams.ri, 0.01, 10_000);
  }

  // Non-ratio models require qi and di
  if (!inRange(dcaParams.qi, 0, 100_000_000)) return false;
  if (!inRange(dcaParams.di, 0, 100)) return false;

  if (model === 'hyperbolic' || model === 'modified_hyperbolic') {
    if (!inRange(dcaParams.b_factor, 0, 10)) return false;
  }
  if (model === 'modified_hyperbolic') {
    if (!inRange(dcaParams.dlim, 0, 100)) return false;
  }

  return true;
}

type Verdict = WorkspaceTask['status'];

// Type Curve Details verdicts normally have to cite an analog-well filter; these
// two don't, so filter attribution stays optional for them.
const FILTER_EVIDENCE_OPTIONAL_PATHS = new Set([
  'type_curve_status',
  'operator_names',
]);

// Normalized equality for a field value vs the task's saved value (draft numbers
// are strings, arrays may be reordered).
function valuesEqual(a: unknown, b: unknown): boolean {
  const norm = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    if (Array.isArray(v)) return JSON.stringify(v.map(String).sort());
    return String(v);
  };
  return norm(a) === norm(b);
}

const sameIdSet = (a: number[], b: number[]): boolean =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

// A field-backed task's coupled form value counts as "missing" when it's null,
// blank, or an empty selection — a verdict can't be attributed to no value.
function isEmptyFieldValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (Array.isArray(v)) return v.length === 0;
  return String(v).trim() === '';
}

// ─── Status Icon ────────────────────────────────────────────────────────────

const TaskStatusIcon = ({ status }: { status: Verdict }) => {
  const tone =
    status === 'correct'
      ? 'success'
      : status === 'incorrect'
        ? 'error'
        : 'neutral';
  const Icon =
    status === 'correct'
      ? CheckmarkFilled
      : status === 'incorrect'
        ? CloseFilled
        : CircleDash;
  return (
    <span
      className={classNames(style.verify, style[`verify-${tone}`])}
      aria-hidden
    >
      <Icon />
    </span>
  );
};

function statusAccentClass(status: Verdict): string {
  switch (status) {
    case 'correct':
      return style['task-success'];
    case 'incorrect':
      return style['task-error'];
    default:
      return style['task-neutral'];
  }
}

// Render the delegated field value defensively — arrays join, null/empty skip.
function formatValue(value: WorkspaceTask['value']): string | null {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  // Composite values (e.g. the Well Control Area object) have no scalar
  // representation — skip the "Value:" line rather than render [object Object].
  if (typeof value === 'object') return null;
  const str = String(value);
  return str.trim() ? str : null;
}

function verdictToCorrectnessState(verdict: Verdict): boolean | null {
  if (verdict === 'correct') return true;
  if (verdict === 'incorrect') return false;
  return null;
}

// ─── Props ──────────────────────────────────────────────────────────────────
// Kept backward-compatible with the two page call sites. The task list now comes
// from GET /tasks; verdict/note edits persist via PATCH .../tasks/:path.

export interface TcVerificationTasksPanelProps {
  workspace: TypeCurveWorkspaceResponse;
  onClose: () => void;
  noTypeCurveArea?: boolean;
  verifiedUnitsCount?: number;
  totalUnitsCount?: number;
  initialExpandedTaskId?: string | null;
  onCreateDCAWorkspace?: () => void;
  // Header-detail id to parent a newly created DCA workspace to (enables the
  // "Create DCA Workspace" action in the DCA Forecasts group).
  dcaParentId?: number;
  /** The DCA scenario selected on the page; a new DCA workspace opens on it. */
  dcaScenarioId?: number | null;
  fittedStreams?: Set<'oil' | 'gas' | 'water'>;
  onGoToTask?: (taskId: string) => void;
  currentDatasetFilters?: Record<string, string[]>;
  filterLabelsById?: Record<string, string>;
  filterContextTaskIds?: Set<string>;
  filterContextOptionalTaskIds?: Set<string>;
  /** Called after a verdict is saved for a task that modifies the header
   *  (e.g. development_type, formation, type_curve_area) so the parent can
   *  refetch the type curve header. */
  onHeaderChanged?: () => void;
}

interface TaskGroupView {
  key: string;
  title: string;
  tasks: WorkspaceTask[];
}

interface TaskDraft {
  verdict: Verdict;
  note: string;
  evidences: number[];
}

// ─── Component ──────────────────────────────────────────────────────────────

/** Task paths whose verdict save modifies the header and should trigger a refetch. */
const HEADER_AFFECTING_PATHS = new Set([
  'development_type',
  'formation',
  'type_curve_area',
  'oil_volumes',
  'gas_volumes',
  'water_volumes',
]);

export const TcVerificationTasksPanel = ({
  workspace,
  onClose,
  dcaParentId,
  dcaScenarioId = null,
  onGoToTask,
  onHeaderChanged,
}: TcVerificationTasksPanelProps) => {
  const { tasks } = useAtomValue(tcWorkspaceTasksAtom);
  // Counts exclude the overall Type Curve task (driven from the header), so use
  // the filtered derived atoms rather than the raw response `.completed`/`.total`.
  const completed = useAtomValue(tcWorkspaceTasksCompletedAtom);
  const total = useAtomValue(tcWorkspaceTasksTotalAtom);
  const savedFilters = useAtomValue(tcSavedWellFiltersAtom);
  const facets = useAtomValue(tcWellsFacetsAtom);
  const detailsDraft = useAtomValue(tcDetailsDraftAtom);
  const filteredUwis = useAtomValue(tcFilteredUwisAtom);
  const dcaFitParams = useAtomValue(tcDcaFitParamsAtom);
  const dcaFitSegments = useAtomValue(tcDcaFitSegmentsAtom);
  const dcaFitAggregation = useAtomValue(tcDcaFitAggregationAtom);
  const dcaFitScenarioId = useAtomValue(tcDcaFitScenarioIdAtom);
  const mapFilterMode = useAtomValue(tcMapFilterModeAtom);
  const mapBounds = useAtomValue(tcMapBoundsAtom);
  const aoiPolygon = useAtomValue(tcAoiPolygonAtom);
  const incrementProbitRefresh = useSetAtom(probitRefreshTriggerAtom);
  const filteredUwiIds = useMemo(
    () => filteredUwis.map((w) => w.uwi),
    [filteredUwis]
  );
  const [expandTaskPath, setExpandTaskPath] = useAtom(tcExpandTaskPathAtom);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  // Unsaved verdict/note/evidence edits, keyed by task path (absent = pristine).
  const [drafts, setDrafts] = useState<Record<string, TaskDraft>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});

  const saveTask = useSaveWorkspaceTask();
  const readOnly = workspace.promoted;

  // Only persisted rows (real ids) can be cited as evidence. Filters, cross plots
  // and colour-by rules all share the analog_well_filter table (distinguished by
  // `kind`); filters and cross plots are valid evidence and get separate lists,
  // colour rules are not evidence at all. Match `kind` explicitly — excluding
  // only 'cross_plot' swept colour rules into the filters list, where they showed
  // as raw rule ids (e.g. "formation-1785528100280-637") and counted toward the
  // filter-attribution requirement.
  const citableFilters = useMemo(
    () => savedFilters.filter((f) => f.id > 0 && f.kind === 'filter'),
    [savedFilters]
  );
  const citableCrossPlots = useMemo(
    () => savedFilters.filter((f) => f.id > 0 && f.kind === 'cross_plot'),
    [savedFilters]
  );
  const citableFilterIds = useMemo(
    () => new Set(citableFilters.map((f) => f.id)),
    [citableFilters]
  );
  const citableIds = useMemo(
    () =>
      new Set([
        ...citableFilters.map((f) => f.id),
        ...citableCrossPlots.map((f) => f.id),
      ]),
    [citableFilters, citableCrossPlots]
  );

  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Bridge: the per-field verify icon requests a task to expand by path.
  useEffect(() => {
    if (!expandTaskPath) return;
    setExpandedPaths((prev) => new Set(prev).add(expandTaskPath));
    setExpandTaskPath(null);
  }, [expandTaskPath, setExpandTaskPath]);

  // Group by backend group, preserving first-seen order (tasks arrive in the
  // catalog's canonical order).
  const groups = useMemo<TaskGroupView[]>(() => {
    const byKey = new Map<string, TaskGroupView>();
    for (const task of tasks) {
      // The overall Type Curve verdict is set from the header, not the checklist.
      if (task.group === OVERALL_TYPE_CURVE_KEY) continue;
      let group = byKey.get(task.group);
      if (!group) {
        group = { key: task.group, title: task.groupTitle, tasks: [] };
        byKey.set(task.group, group);
      }
      group.tasks.push(task);
    }
    return [...byKey.values()];
  }, [tasks]);

  const toggleExpand = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const clearDraft = useCallback((path: string) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
    setSaveErrors((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  }, []);

  const handleSave = useCallback(
    (
      task: WorkspaceTask,
      verdict: Verdict,
      note: string,
      evidences: number[]
    ) => {
      setSaveErrors((prev) => {
        const next = { ...prev };
        delete next[task.path];
        return next;
      });
      const fieldEntry = FIELD_TASK_BY_PATH[task.path];
      let header: { key: string; value: unknown } | undefined = undefined;
      if (fieldEntry) {
        const rawValue = detailsDraft
          ? (detailsDraft as unknown as Record<string, unknown>)[
              fieldEntry.draftKey
            ]
          : undefined;
        // Every field is single-select (send a scalar) EXCEPT operators, which
        // is multi-select (send the array). The draft stores developmentType as
        // a 1-element array, so unwrap it here.
        const value =
          fieldEntry.headerKey !== 'operator_names' && Array.isArray(rawValue)
            ? (rawValue[0] ?? null)
            : rawValue;
        header = { key: fieldEntry.headerKey, value };
      }
      // Well Control Area is field-backed by the composite Global Map Filter
      // selection — build its header from the current map-filter atoms rather
      // than the details-form draft. Omit when the selected mode has no spatial
      // data yet (avoids a backend 422 on an empty geometry).
      if (task.path === 'well_control_area') {
        const wcaValue = buildWellControlAreaValue(
          mapFilterMode,
          mapBounds,
          aoiPolygon
        );
        header = wcaValue
          ? { key: 'well_control_area', value: wcaValue }
          : undefined;
      }
      // Type Curve Volumes tasks carry the matching stream's latest DCA fit
      // hyper-params (from the Fit button), included silently when present.
      let dcaForecasts;
      if (task.group === 'type_curve_volumes') {
        const stream = task.path.replace(/_volumes$/, '');
        const dcaParams = (
          dcaFitParams as Record<string, Record<string, unknown> | undefined>
        )[stream];
        if (dcaParams && isPersistedDcaParamsValid(dcaParams)) {
          // Strip invalid/empty ramp_up so we don't send zeroed-out values
          const cleanedParams = { ...dcaParams };
          if (!isPersistedRampUpValid(cleanedParams.ramp_up)) {
            cleanedParams.ramp_up = null;
          }

          // Attach the fit's segmentation / outlier month-online indices too,
          // silently, only when the fit produced any.
          const seg = (
            dcaFitSegments as Record<string, StreamFitSegments | undefined>
          )[stream];
          dcaForecasts = [
            {
              stream,
              dca_params: cleanedParams,
              ...(dcaFitScenarioId[stream as MineralType]
                ? { dca_scenario_id: dcaFitScenarioId[stream as MineralType] }
                : {}),
              aggregation:
                (dcaFitAggregation as Record<string, string | undefined>)[
                  stream
                ] ?? 'p50',
              ...(seg?.segmentMonthsOnline.length
                ? { segment_months_online: seg.segmentMonthsOnline }
                : {}),
              ...(seg?.outlierMonthsOnline.length
                ? { outlier_months_online: seg.outlierMonthsOnline }
                : {}),
            },
          ];
        }
      }
      saveTask.mutate(
        {
          path: task.path,
          correctnessState: verdictToCorrectnessState(verdict),
          reason: note,
          evidences,
          header,
          dcaForecasts,
        },
        {
          onSuccess: () => {
            clearDraft(task.path);
            if (HEADER_AFFECTING_PATHS.has(task.path)) {
              onHeaderChanged?.();
            }
            if (task.group === 'type_curve_volumes') {
              incrementProbitRefresh((prev) => prev + 1);
            }
          },
          onError: async (err) => {
            const message =
              (await parseTaskError(err)) ?? 'Could not save. Try again.';
            setSaveErrors((prev) => ({ ...prev, [task.path]: message }));
          },
        }
      );
    },
    [
      saveTask,
      clearDraft,
      detailsDraft,
      dcaFitParams,
      dcaFitSegments,
      dcaFitAggregation,
      dcaFitScenarioId,
      mapFilterMode,
      mapBounds,
      aoiPolygon,
      onHeaderChanged,
      incrementProbitRefresh,
    ]
  );

  return (
    <div className={style.root}>
      <div className={style.panelHeader}>
        <div className={style.headerCopy}>
          <h2 className={style.title}>Tasks</h2>
          <p className={style.subtitle}>
            {completed} of {total} tasks
          </p>
        </div>
        <Button
          hasIconOnly
          size="lg"
          kind="ghost"
          onClick={onClose}
          iconDescription="Close tasks panel"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(iconProps) => <Close size={20} {...iconProps} />}
        />
      </div>

      <div className={style.progressWrap}>
        <ProgressBar
          label="Verification progress"
          hideLabel
          value={pct}
          size="small"
        />
      </div>

      <div className={style.panelBody}>
        {groups.map((group) => {
          const groupDone = group.tasks.filter((t) => t.status !== null).length;

          return (
            <div key={group.key} className={style.category}>
              <Accordion defaultExpanded>
                <AccordionSummary>
                  <div className={style.categorySummary}>
                    <span className={style.categoryLabel}>{group.title}</span>
                    <span className={style.categoryCount}>
                      {groupDone}/{group.tasks.length}
                    </span>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  {group.key === 'dca_forecasts' && dcaParentId ? (
                    <div className={style.groupCta}>
                      <CreateDcaWorkspace
                        parentType="TypeCurve::HeaderDetail"
                        parentId={dcaParentId}
                        additionalData={{ uwis: filteredUwiIds, dcaScenarioId }}
                        disabled={readOnly || filteredUwiIds.length === 0}
                      />
                    </div>
                  ) : null}

                  {group.tasks.map((task) => {
                    const isExpanded = expandedPaths.has(task.path);

                    const savedNote = task.note ?? '';
                    // Saved evidence, limited to filters that still exist.
                    const savedEvidences = task.linkedFilterIds.filter((id) =>
                      citableIds.has(id)
                    );

                    const draft = drafts[task.path];
                    const verdict: Verdict = draft
                      ? draft.verdict
                      : task.status;
                    const note = draft ? draft.note : savedNote;
                    const evidences = draft ? draft.evidences : savedEvidences;

                    // Field-backed tasks: current form value + whether it's a
                    // value that must be attributed to ≥1 filter.
                    const fieldEntry = FIELD_TASK_BY_PATH[task.path];
                    const fieldValue =
                      fieldEntry && detailsDraft
                        ? (detailsDraft as unknown as Record<string, unknown>)[
                            fieldEntry.draftKey
                          ]
                        : undefined;
                    const valueChanged =
                      !!fieldEntry && !valuesEqual(fieldValue, task.value);
                    // Show what the user is about to save, not the persisted
                    // value; falls back to the saved one when there's no draft
                    // (read-only page) or the task isn't field-backed.
                    const value = formatValue(
                      fieldValue === undefined
                        ? task.value
                        : (fieldValue as WorkspaceTask['value'])
                    );
                    // A field-backed task needs a valid value in its coupled
                    // Type Curve Details input before a verdict can be set — you
                    // can't mark an empty field Correct/Incorrect.
                    const fieldValueMissing =
                      !!fieldEntry &&
                      verdict !== null &&
                      isEmptyFieldValue(fieldValue);
                    // Type Curve Details verdicts must attribute the value to at
                    // least one linked filter before saving (any filter counts).
                    const isDetailsGroup = task.group === 'type_curve_details';
                    // Only filter-kind evidence satisfies the requirement; cross
                    // plots are optional evidence and don't count here.
                    const filterEvidenceCount = evidences.filter((id) =>
                      citableFilterIds.has(id)
                    ).length;
                    const filterRequiredMissing =
                      isDetailsGroup &&
                      !FILTER_EVIDENCE_OPTIONAL_PATHS.has(task.path) &&
                      verdict !== null &&
                      filterEvidenceCount === 0;

                    const dirty =
                      task.status === null
                        ? verdict !== null
                        : verdict !== task.status ||
                          note !== savedNote ||
                          !sameIdSet(evidences, savedEvidences) ||
                          valueChanged;
                    const isSaving =
                      saveTask.isPending &&
                      saveTask.variables?.path === task.path;

                    const setDraft = (next: Partial<TaskDraft>) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [task.path]: { verdict, note, evidences, ...next },
                      }));

                    // Re-clicking the active verdict clears it (unverified).
                    const pickVerdict = (v: Exclude<Verdict, null>) =>
                      setDraft({ verdict: verdict === v ? null : v });

                    const toggleEvidence = (id: number, checked: boolean) =>
                      setDraft({
                        evidences: checked
                          ? [...evidences, id]
                          : evidences.filter((e) => e !== id),
                      });

                    return (
                      <div
                        key={task.path}
                        className={classNames(
                          style.task,
                          statusAccentClass(task.status)
                        )}
                      >
                        <div
                          className={style.taskRow}
                          onClick={() => toggleExpand(task.path)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleExpand(task.path);
                            }
                          }}
                        >
                          <TaskStatusIcon status={task.status} />
                          <div className={style.taskInfo}>
                            <span className={style.taskLabel}>
                              {task.title}
                            </span>
                          </div>
                          {task.required ? (
                            <Tag
                              type="blue"
                              size="sm"
                              className={style.requiredTag}
                            >
                              Required
                            </Tag>
                          ) : null}
                          {task.note ? (
                            <DocumentBlank
                              size={14}
                              className={style.noteIndicator}
                              aria-label="Has note"
                            />
                          ) : null}
                          {onGoToTask ? (
                            <Button
                              hasIconOnly
                              kind="ghost"
                              size="sm"
                              className={style.goToBtn}
                              iconDescription="Go to task"
                              tooltipPosition="left"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onGoToTask(task.path);
                              }}
                              renderIcon={(iconProps) => (
                                <View size={16} {...iconProps} />
                              )}
                            />
                          ) : null}
                          <ChevronDown
                            size={16}
                            className={classNames(style.expandIcon, {
                              [style.expandIconOpen]: isExpanded,
                            })}
                            aria-hidden
                          />
                        </div>

                        {isExpanded ? (
                          <>
                            {task.description ? (
                              <div className={style.verdictPicker}>
                                <p className={style.taskDesc}>
                                  {task.description}
                                </p>
                              </div>
                            ) : null}

                            {value ? (
                              <div className={style.verdictPicker}>
                                <p className={style.taskDesc}>
                                  <strong>Value:</strong> {value}
                                </p>
                              </div>
                            ) : null}

                            <div className={style.verdictPicker}>
                              {saveErrors[task.path] ? (
                                <InlineNotification
                                  kind="error"
                                  title="Save failed"
                                  subtitle={saveErrors[task.path]}
                                  lowContrast
                                  hideCloseButton
                                  className={style.errorNote}
                                />
                              ) : null}

                              <div>
                                <span className={style.verdictLabel}>
                                  Verdict
                                </span>
                                <div className={style.verdictButtons}>
                                  <Button
                                    kind={
                                      verdict === 'correct'
                                        ? 'primary'
                                        : 'ghost'
                                    }
                                    size="sm"
                                    disabled={readOnly || isSaving}
                                    onClick={() => pickVerdict('correct')}
                                  >
                                    Correct
                                  </Button>
                                  <Button
                                    kind={
                                      verdict === 'incorrect'
                                        ? 'danger'
                                        : 'ghost'
                                    }
                                    size="sm"
                                    disabled={readOnly || isSaving}
                                    onClick={() => pickVerdict('incorrect')}
                                  >
                                    Incorrect
                                  </Button>
                                </div>
                              </div>

                              {!readOnly && fieldValueMissing ? (
                                <p className={style.noteRequiredHint}>
                                  Enter a value in the {task.title} field before
                                  saving a verdict.
                                </p>
                              ) : null}

                              {readOnly ? (
                                <p className={style.lockedHint}>
                                  This workspace is promoted; verification is
                                  locked.
                                </p>
                              ) : null}
                            </div>

                            {/* Filters used — evidence for Type Curve Details
                                tasks only (like Cross Plots below). */}
                            {isDetailsGroup ? (
                              <div className={style.filterContext}>
                                <span className={style.verdictLabel}>
                                  Filters used
                                </span>
                                {citableFilters.length === 0 ? (
                                  <p className={style.taskDesc}>
                                    No filters applied yet.
                                  </p>
                                ) : (
                                  <div className={style.filterCheckboxList}>
                                    {citableFilters.map((filter) => (
                                      <Checkbox
                                        key={filter.id}
                                        id={`task-${task.path}-filter-${filter.id}`}
                                        labelText={labelForFilterRow(
                                          filter,
                                          facets
                                        )}
                                        checked={evidences.includes(filter.id)}
                                        disabled={readOnly || isSaving}
                                        onChange={(_evt, { checked }) =>
                                          toggleEvidence(filter.id, checked)
                                        }
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : null}

                            {/* Cross Plots — optional evidence, Type Curve
                                Details tasks only. Same evidences array. */}
                            {isDetailsGroup ? (
                              <div className={style.filterContext}>
                                <span className={style.verdictLabel}>
                                  Cross Plots
                                </span>
                                {citableCrossPlots.length === 0 ? (
                                  <p className={style.taskDesc}>
                                    No cross plots created yet.
                                  </p>
                                ) : (
                                  <div className={style.filterCheckboxList}>
                                    {citableCrossPlots.map((cp) => (
                                      <Checkbox
                                        key={cp.id}
                                        id={`task-${task.path}-crossplot-${cp.id}`}
                                        labelText={
                                          (cp.definition?.title as string) ||
                                          cp.name
                                        }
                                        checked={evidences.includes(cp.id)}
                                        disabled={readOnly || isSaving}
                                        onChange={(_evt, { checked }) =>
                                          toggleEvidence(cp.id, checked)
                                        }
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : null}

                            <div className={style.noteSection}>
                              <Layer>
                                <TextArea
                                  id={`task-note-${task.path}`}
                                  labelText="Note"
                                  placeholder={`Add a note about "${task.title}"...`}
                                  value={note}
                                  onChange={(e) =>
                                    setDraft({ note: e.target.value })
                                  }
                                  disabled={readOnly || isSaving}
                                />
                              </Layer>
                            </div>

                            {filterRequiredMissing ? (
                              <div className={style.warningCard}>
                                Pick at least one filter to attribute to this
                                value before saving.
                              </div>
                            ) : null}

                            <div className={style.noteActions}>
                              <Button
                                kind="primary"
                                size="md"
                                onClick={() =>
                                  handleSave(task, verdict, note, evidences)
                                }
                                disabled={
                                  readOnly ||
                                  isSaving ||
                                  !dirty ||
                                  fieldValueMissing ||
                                  filterRequiredMissing
                                }
                              >
                                {isSaving ? 'Saving…' : 'Save'}
                              </Button>
                              <Button
                                kind="secondary"
                                size="md"
                                onClick={() => clearDraft(task.path)}
                                disabled={isSaving || !dirty}
                              >
                                Cancel
                              </Button>
                            </div>

                            <div className={style.collapseRow}>
                              <button
                                type="button"
                                className={style.collapseLink}
                                onClick={() => toggleExpand(task.path)}
                              >
                                Collapse
                              </button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            </div>
          );
        })}
      </div>
    </div>
  );
};
